import { t } from '@transifex/native';

import { show } from 'pkg/actions/flashes';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { create } from 'pkg/api/models/actions';
import * as sdk from 'pkg/core/sdk';

import { Event, trackEvent, trackedGroup } from 'components/analytics';

interface PerformanceReviewPayload {
	id?: number;
	performanceReview?: string;
	userId: number;
	groupId: number;
}

export async function deletePerformanceReview(
	organizationId: number,
	id: number
): Promise<void> {
	const res = await sdk.destroy(
		endpoints.UserGroupPerformanceReviews.Delete(organizationId, id),
		{},
		{}
	);

	if (res.ok) {
		show(
			{
				title: '',
				message: t(`Deleted player dialogue`),
			},
			200
		);
	}
}

export async function updatePerformanceReview(
	organizationId: number,
	payload: PerformanceReviewPayload
): Promise<void> {
	const response = await sdk.patch(
		endpoints.UserGroupPerformanceReviews.Update(organizationId, payload.id),
		{},
		payload
	);

	if (!response.ok) {
		throw response;
	}

	show(
		{
			title: '',
			message: t(`Performance review updated successfully`),
		},
		200
	);
}

export async function savePerformanceReview(
	organizationId: number,
	payload: PerformanceReviewPayload
): Promise<models.performanceReview.PerformanceReview> {
	const p = payload?.performanceReview?.trim();

	if (!p) {
		show({
			title: t(`Save failed`),
			message: t(`Failed to save, make sure you have written something.`),
		});

		return null;
	}

	const [request, review] = await create<
		PerformanceReviewPayload,
		models.performanceReview.PerformanceReview
	>(endpoints.UserGroupPerformanceReviews.Create(organizationId), payload);

	if (!request.ok) {
		throw request;
	}

	trackEvent(Event.PerformanceReviewCreated, trackedGroup(payload.groupId));

	show(
		{
			title: '',
			message: t(`Player dialogue saved successfully`),
		},
		200
	);

	return review;
}

export async function addAttachments(
	organizationId: number,
	reviewId: number,
	attachments: models.attachment.Attachment[]
): Promise<void> {
	await Promise.allSettled(
		attachments.map(async (attachment) => {
			return await sdk.post(
				endpoints.UserGroupPerformanceReviews.AddAttachment(
					organizationId,
					reviewId,
					attachment.id
				)
			);
		})
	);
}

export async function removeAttachment(
	organizationId: number,
	reviewId: number,
	attachmentId: number
): Promise<boolean> {
	const response = await sdk.destroy(
		endpoints.UserGroupPerformanceReviews.RemoveAttachment(
			organizationId,
			reviewId,
			attachmentId
		),
		{},
		{}
	);

	return response.ok;
}
